import React, { useState } from "react";
import getAxios from "../../hooks/useAxios";
import Cookies from "js-cookie";
import {
  Box,
  Button,
  useTheme,
  Text,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { FaArrowRight } from "react-icons/fa";
import { Theme } from "../../theme";
import { useNavigate } from "react-router-dom";
import Input from "../../components/CustomInput";

type CouponPayload = {
  code: string;
  start_at: string;
  end_at: string;
  flat_discount?: number;
  discount_percent?: number;
  max_usage: number;
};

const CreateCoupon = () => {
  const theme = useTheme<Theme>();
  const navigate = useNavigate();

  const axios = getAxios();
  const access_token = Cookies.get("x-admin-auth");

  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState("flat"); // 'flat' or 'percent'
  const [flatDiscount, setFlatDiscount] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [status, setStatus] = useState("");
  const [maxUsage, setMaxUsage] = useState<number>();

  const handleSubmit = async () => {
    // Clear previous status
    setStatus("");

    // Code must be exactly 8 characters
    // if (!/^[a-zA-Z0-9]{8}$/.test(code)) {
    //   setStatus("Coupon code must be exactly 8 alphanumeric characters.");
    //   return;
    // }

    // Discount validation
    if (discountType === "flat") {
      if (!flatDiscount || parseFloat(flatDiscount) <= 0) {
        setStatus("Flat discount must be a positive number.");
        return;
      }
    } else {
      if (
        !discountPercent ||
        parseFloat(discountPercent) <= 0 ||
        parseFloat(discountPercent) > 100
      ) {
        setStatus("Discount percent must be between 1 and 100.");
        return;
      }
    }

    // Date validations
    if (!startAt || !endAt) {
      setStatus("Start and end dates are required.");
      return;
    }

    const startDate = new Date(startAt);
    const endDate = new Date(endAt);

    if (startDate >= endDate) {
      setStatus("End date must be after the start date.");
      return;
    }

    // Prepare payload
    const couponData: CouponPayload = {
      code,
      start_at: startDate.toISOString(),
      end_at: endDate.toISOString(),
      max_usage: Number(maxUsage),
    };

    if (discountType === "flat") {
      couponData.flat_discount = parseFloat(flatDiscount);
    } else {
      couponData.discount_percent = parseFloat(discountPercent);
    }

    // Send request
    try {
      const response = await axios.post(`/coupons`, couponData, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
      setStatus("Coupon created successfully!");
      console.log(response.data);
      setTimeout(() => {
        navigate("/coupons");
      }, 2000);
    } catch (error) {
      console.error("Error creating coupon:", error);
      setStatus("Failed to create coupon.");
    }
  };

  return (
    <Box style={{ padding: "2rem" }}>
      <Text
        sx={{
          fontSize: theme.fontSizes.subHeading2,
          marginBottom: theme.space["3xl"],
        }}
      >
        Create Coupon
      </Text>
      <Box
        style={{
          width: "50%",
          backgroundColor: "#FFFFFF",
          padding: "2rem",
          borderRadius: "8px",
        }}
      >
        <label>Coupon code:</label>
        <Input
          type="text"
          placeholder="Coupon Code (8 characters)"
          maxLength={8}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          variant={"outline"}
        />

        <label style={{ display: "block", marginTop: theme.space.xl }}>
          Usage Limit
        </label>
        <Input
          type="number"
          placeholder="Enter maximum of coupon usage limit"
          maxLength={8}
          value={maxUsage}
          onChange={(e) => setMaxUsage(Number(e.target.value))}
          variant={"outline"}
        />

        <label style={{ display: "block", marginTop: theme.space.xl }}>
          Discount Type:
        </label>
        <div style={{ marginBottom: "10px" }}>
          <label style={{ marginRight: "10px" }}>
            <input
              type="radio"
              name="discountType"
              value="flat"
              checked={discountType === "flat"}
              onChange={() => setDiscountType("flat")}
            />
            Flat Discount (₹)
          </label>
          <label>
            <input
              type="radio"
              name="discountType"
              value="percent"
              checked={discountType === "percent"}
              onChange={() => setDiscountType("percent")}
            />
            Percent Discount (%)
          </label>
        </div>

        {discountType === "flat" && (
          <Input
            type="number"
            placeholder="Flat Discount in ₹"
            value={flatDiscount}
            onChange={(e) => setFlatDiscount(e.target.value)}
            variant={"outline"}
          />
        )}

        {discountType === "percent" && (
          <Input
            type="number"
            placeholder="Discount Percent in %"
            value={discountPercent}
            onChange={(e) => setDiscountPercent(e.target.value)}
            variant={"outline"}
          />
        )}

        <label style={{ display: "block", marginTop: theme.space.xl }}>
          Start At:
        </label>
        <Input
          type="datetime-local"
          value={startAt}
          onChange={(e) => setStartAt(e.target.value)}
          variant={"outline"}
        />
        <label style={{ display: "block", marginTop: theme.space.xl }}>
          End At:
        </label>
        <Input
          type="datetime-local"
          value={endAt}
          onChange={(e) => setEndAt(e.target.value)}
          variant={"outline"}
        />

        <Box sx={{ marginTop: theme.space.xxl }}>
          <Button
            type="submit"
            onClick={handleSubmit}
            width="100%"
            height="3rem"
            rightIcon={<FaArrowRight />}
            // isLoading={isLoginLoading ? true : false}
            style={{
              background: theme.colors.primary.main,
              color: theme.colors.bg.main,
            }}
          >
            <Text sx={{ fontSize: theme.fontSizes.body }}>Create</Text>
          </Button>
        </Box>
        {status && (
          <Alert status="success">
            <AlertIcon />
            {status}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default CreateCoupon;
